import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import CookieJs from "js-cookie";
import dayjs, { Dayjs } from "dayjs";
import get from "lodash/get";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import * as yup from "yup";
import useNotification from "../hooks/useNotification";
import plService from "../services/pl";
import queryDefault from "../utils/queryDefault";

type Props = {
  open: boolean;
  id: number | null;
  branchGroups: any[];
  onClose: () => void;
  onSaved?: () => void;
};

const schema = yup.object().shape({
  branch_group_id: yup
    .number()
    .typeError("branch group is a required field")
    .moreThan(0, "branch group is a required field")
    .required("branch group is a required field"),
  transaction_date: yup.string().required("transaction date is a required field"),
  amount: yup
    .number()
    .typeError("amount is a required field")
    .moreThan(0, "amount must be greater than 0")
    .required("amount is a required field"),
  description: yup
    .string()
    .max(500, "description must be at most 500 characters")
    .required("description is a required field"),
});

const formatDate = (value: Dayjs | null, fallback: string) => {
  if (!value || !value.isValid()) {
    return fallback;
  }

  return value.format("YYYY-MM-DD");
};

export default function PLBepDetailModal({
  open,
  id,
  branchGroups,
  onClose,
  onSaved,
}: Props) {
  const notification = useNotification();
  const user = JSON.parse(CookieJs.get("USER") || "{}");
  const [transactionDate, setTransactionDate] = useState<Dayjs | null>(dayjs());

  const form = useForm({
    defaultValues: {
      branch_group_id: 0,
      transaction_date: dayjs().format("YYYY-MM-DD"),
      amount: 0,
      description: "",
    },
    resolver: yupResolver(schema),
  });

  const bepDetail = useQuery(
    ["pl-bep-detail", id],
    () => plService.getBEPDetail(id as number),
    {
      ...queryDefault,
      enabled: Boolean(open && id),
    }
  );
  const bepDetailData = bepDetail.data?.data;

  const createBepDetail = useMutation("create-pl-bep-detail", plService.createBEPDetail, {
    onSuccess: (response: any) => {
      notification.onOpen({
        message: response?.message || "BEP detail was successfully created!",
        type: "success",
        position: "top",
      });
      if (onSaved) {
        onSaved();
      }
      onClose();
    },
    onError: (error: any) => {
      notification.onOpen({
        message: error?.response?.data?.message || "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const updateBepDetail = useMutation(
    "update-pl-bep-detail",
    ({ payloadId, payload }: { payloadId: number; payload: any }) =>
      plService.updateBEPDetail(payloadId, payload),
    {
      onSuccess: (response: any) => {
        notification.onOpen({
          message: response?.message || "BEP detail was successfully updated!",
          type: "success",
          position: "top",
        });
        if (onSaved) {
          onSaved();
        }
        onClose();
      },
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Something went wrong",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!id) {
      const today = dayjs();
      form.reset({
        branch_group_id: 0,
        transaction_date: today.format("YYYY-MM-DD"),
        amount: 0,
        description: "",
      });
      setTransactionDate(today);
      return;
    }

    if (bepDetailData) {
      const data = bepDetailData;
      const currentTransactionDate = String(
        get(data, "transaction_date", dayjs().format("YYYY-MM-DD"))
      );

      form.reset({
        branch_group_id: Number(get(data, "branch_group_id", 0)),
        transaction_date: currentTransactionDate,
        amount: Number(get(data, "amount", 0)),
        description: String(get(data, "description", "")),
      });
      setTransactionDate(dayjs(currentTransactionDate));
    }
  }, [bepDetailData, form, id, open]);

  const onSubmit = (values: any) => {
    const payload = {
      ...values,
      username: user?.username || user?.name || "",
    };

    if (id) {
      updateBepDetail.mutate({
        payloadId: id,
        payload,
      });
      return;
    }

    createBepDetail.mutate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{id ? "Edit BEP Detail" : "Create BEP Detail"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Branch Group</FormLabel>
              <Controller
                name="branch_group_id"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    size="small"
                    inputRef={ref}
                    value={field.value ?? 0}
                    error={Boolean(fieldState.error?.message)}
                    helperText={fieldState.error?.message}
                    onChange={(event) => {
                      form.setValue("branch_group_id", Number(event.target.value), {
                        shouldValidate: true,
                      });
                    }}
                  >
                    <MenuItem value={0}>Select Branch Group</MenuItem>
                    {branchGroups.map((item: any) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Transaction Date</FormLabel>
              <Controller
                name="transaction_date"
                control={form.control}
                render={({ fieldState }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      inputFormat="DD/MM/YYYY"
                      value={transactionDate}
                      onChange={(newValue) => {
                        setTransactionDate(newValue);
                        form.setValue(
                          "transaction_date",
                          formatDate(newValue, form.getValues().transaction_date),
                          { shouldValidate: true }
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          error={Boolean(fieldState.error?.message)}
                          helperText={fieldState.error?.message || params?.helperText}
                        />
                      )}
                    />
                  </LocalizationProvider>
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Amount</FormLabel>
              <Controller
                name="amount"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    type="number"
                    size="small"
                    inputRef={ref}
                    error={Boolean(fieldState.error?.message)}
                    helperText={fieldState.error?.message}
                    onChange={(event) => {
                      const value = event.target.value;
                      form.setValue("amount", value === "" ? 0 : Number(value), {
                        shouldValidate: true,
                      });
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel required>Description</FormLabel>
              <Controller
                name="description"
                control={form.control}
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    size="small"
                    multiline
                    minRows={3}
                    inputProps={{ maxLength: 500 }}
                    error={Boolean(fieldState.error?.message)}
                    helperText={fieldState.error?.message || `${String(field.value || "").length}/500`}
                  />
                )}
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={form.handleSubmit(onSubmit)}
          loading={createBepDetail.isLoading || updateBepDetail.isLoading || bepDetail.isLoading}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}